<app-page>
    <app-breadcrumbs [crumbs]="crumbs"></app-breadcrumbs>

    @if (!isAll()) {
        <section class="platform-card">
            <!-- TODO(design): company name above the platform name; GET platforms/:id does not load the company relation (add 'company' to PlatformController::show) -->
            <app-page-header [eyebrow]="platform().company?.name" [title]="platform().name">
                @if (summary(); as summary) {
                    <app-status-badge [tone]="summary.tone" after-title>{{ summary.label }}</app-status-badge>
                }
                @if (summary()?.sub) {
                    <span class="summary-sub">{{ summary().sub }}</span>
                }
                @if (Features.PlatformsSave | hasFeature) {
                    <a actions mat-stroked-button routerLink="/{{Routenames.platforms}}/{{platform().id}}"><i class="fa-regular fa-pen me-1"></i> Bewerken</a>
                }
                @if (Features.CheckpointsSave | hasFeature) {
                    <a actions mat-flat-button routerLink="/{{Routenames.seire}}/{{platform().id}}/store/new"><i class="fa-regular fa-plus me-1"></i> Checkpoint toevoegen</a>
                }
            </app-page-header>
            @if (envUrls().length) {
                <div class="env-strip">
                    @for (env of envUrls(); track env.env) {
                        <div class="env-cell">
                            <app-env-badge [env]="env.env" showName></app-env-badge>
                            <a [attr.title]="env.url" [href]="env.url" class="ui-mono ui-ellipsis" rel="noopener" target="_blank">{{ env.label }}</a>
                        </div>
                    }
                </div>
            }
        </section>
    }

    <app-section [meta]="checkpointCount()" [title]="isAll() ? 'Alle checkpoints' : 'Checkpoints'" flush icon="bullseye" layout="stacked">
        <app-tab-group [(value)]="levelFilter" [options]="levelOptions()" actions label="Level" size="sm"></app-tab-group>

        <app-data-list [columns]="isAll()
                    ? '112px minmax(0,1fr) minmax(0,.8fr) minmax(0,1.6fr) 96px 112px 84px 110px 16px'
                    : '112px minmax(0,1fr) minmax(0,1.8fr) 96px 112px 84px 110px 16px'"
                       [minWidth]="isAll() ? '1100px' : '960px'">
            <div app-data-row chevron header>
                <span>Status</span><span>Naam</span>
                @if (isAll()) {
                    <span>Platform</span>
                }
                <span>URL</span><span>Level</span><span>Laatste check</span><span>SSL verloopt</span><span>Opgeschort tot</span>
            </div>
            @for (row of visibleRows(); track row.checkpoint.id) {
                <!-- Whole row links to the checkpoint overview (stretched link), the URL and platform stay separate links -->
                <div [tone]="row.status.rank === 0 ? 'error' : null" app-data-row chevron class="clickable position-relative">
                    <app-status-dot [problem]="row.problem" [tone]="row.status.tone">{{ row.status.label }}</app-status-dot>
                    <a [routerLink]="['/', Routenames.seire, row.checkpoint.platform_id, 'overview', row.checkpoint.id]" class="row-link stretched-link">{{ row.checkpoint.name }}</a>
                    @if (isAll()) {
                        <a [routerLink]="['/', Routenames.seire, row.checkpoint.platform_id]" class="platform-link ui-ellipsis position-relative z-2">{{ platformName(row.checkpoint.platform_id) }}</a>
                    }
                    <span class="url-cell">
                        @if (row.checkpoint.url) {
                            <a [attr.title]="row.checkpoint.url" [href]="row.checkpoint.url" class="checkpoint-url position-relative z-2" rel="noopener" target="_blank">
                                <span class="ui-cell ui-mono">
                                    <span class="url-main">{{ row.main }}</span>
                                    @if (row.host) {
                                        <span class="url-host ui-ellipsis">{{ row.host }}</span>
                                    }
                                </span>
                                <i class="fa-regular fa-arrow-up-right-from-square"></i>
                            </a>
                        } @else {
                            <span class="ui-faint">—</span>
                        }
                    </span>
                    <span><app-status-badge [tone]="levelTones[row.checkpoint.level] ?? 'neutral'" variant="tag">{{ row.checkpoint.level }}</app-status-badge></span>
                    <span class="ui-muted cell-sm">{{ row.checkpoint.updated_at | lastCheckReadable }}</span>
                    <span [class.ui-error]="row.sslTone === 'error'" [class.ui-faint]="!row.checkpoint.ssl_valid_till" [class.ui-warn]="row.sslTone === 'warn'" class="ui-mono cell-sm">
                        {{ (row.checkpoint.ssl_valid_till | daysFromNow) ?? '—' }}
                    </span>
                    <span [class.ui-faint]="!row.checkpoint.postponed_till" class="cell-sm">{{ (row.checkpoint.postponed_till | date:'EEE d MMM HH:mm') ?? '—' }}</span>
                </div>
            } @empty {
                <app-empty-state>{{ checkpoints() === null ? 'Aan het laden...' : 'Geen checkpoints gevonden' }}</app-empty-state>
            }
        </app-data-list>
    </app-section>

    @if (!isAll()) {
        <app-section [meta]="serversSubtitle()" flush icon="server" layout="stacked" title="Servers">
            @for (card of serverCards(); track card.display.server.ip) {
                <div class="server-card">
                    <div class="server-head">
                        <span class="ui-cell">
                            <span class="server-name">{{ card.display.server.server_name }}</span>
                            <span class="ui-mono ui-meta">{{ card.display.server.ip }}</span>
                        </span>
                        <span class="d-flex flex-wrap gap-2">
                            @for (env of card.envs; track env) {
                                <app-env-badge [env]="env" class="env-chip" showName></app-env-badge>
                            }
                        </span>
                        <span class="ui-cell align-items-end ms-auto">
                            <span [class.stale]="card.display.stale">Bijgewerkt {{ card.display.server.updated_at | relativeDay | lowercase }}</span>
                            <span class="ui-meta ui-faint">{{ card.display.server.updated_at | date:'EEE d MMM HH:mm' }}</span>
                        </span>
                    </div>
                    <div class="row row-cols-2 row-cols-sm-3 row-cols-lg-5 g-2">
                        <div class="col">
                            <div class="spec">
                                <span class="spec-label">Apache</span>
                                <span class="spec-value">{{ card.display.server.apache }}</span>
                                <span class="spec-sub">Module: {{ card.display.server.apache_module }}</span>
                            </div>
                        </div>
                        <div class="col">
                            <div [class.warn]="card.display.phpDiffers" class="spec">
                                <span class="spec-label">PHP</span>
                                <span class="spec-value">{{ card.display.server.php }}</span>
                                <span class="spec-sub">{{ card.display.phpDiffers ? 'Apache gebruikt ' + card.display.server.apache_php : 'Apache gelijk' }}</span>
                            </div>
                        </div>
                        <div class="col">
                            <!-- TODO(design): the design shows "Ubuntu 24.04" with "LTS" below; the server only reports the release number (lsb_release -sr) -->
                            <div [class.warn]="card.display.osOutdated" class="spec">
                                <span class="spec-label">OS</span>
                                <span class="spec-value">{{ card.display.server.os }}</span>
                            </div>
                        </div>
                        <div class="col">
                            <div class="spec">
                                <span class="spec-label">MySQL</span>
                                <span class="spec-value">{{ card.display.server.mysql }}</span>
                            </div>
                        </div>
                        <div class="col">
                            <div [class.warn]="card.display.manyUpdates" class="spec">
                                <span class="spec-label">Updates</span>
                                <span class="spec-value">{{ card.display.server.updates }}</span>
                                <span class="spec-sub">Beschikbaar</span>
                            </div>
                        </div>
                    </div>
                </div>
            } @empty {
                <app-empty-state>Geen servers beschikbaar</app-empty-state>
            }
        </app-section>
    }
</app-page>
